import { useBusiness } from '@/contexts/BusinessContext'
import { ChevronLeft, Search } from 'lucide-react'
import { useState, type ComponentType } from 'react'
import { Link } from 'react-router-dom'

import { cn } from '@/lib/utils'

import { StoreAvatar } from './AccountMenu'

export interface SectionLink {
  to: string
  label: string
  icon?: ComponentType<{ className?: string }>
  /** Indented under another item (delivery methods and areas under shipping). */
  nested: boolean
  active: boolean
}

interface SectionNavProps {
  /** "‹ Settings", "‹ Point of sale". */
  title: string
  /** Where the title link goes: the last page visited outside the section. */
  exitTo: string
  links: SectionLink[]
  /** Called after a link is followed (closes the mobile sheet). */
  onNavigate?: () => void
}

/**
 * Shopify's section column (§6.10), shown beside the icon rail in settings and point of sale: a way back, a search
 * that filters the list, the store card and the section's pages.
 */
export const SectionNav = ({ title, exitTo, links, onNavigate }: SectionNavProps) => {
  const { profile } = useBusiness()
  const [query, setQuery] = useState('')
  const domain = profile?.custom_customer_domain || profile?.platform_customer_domain
  const q = query.trim().toLowerCase()
  const shown = q ? links.filter((l) => l.label.toLowerCase().includes(q)) : links
  const searchLabel = `Search ${title.toLowerCase()}`

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-[52px] shrink-0 items-center px-3.5">
        <Link
          to={exitTo}
          onClick={onNavigate}
          className="flex h-7 items-center gap-1.5 rounded-md pl-1 pr-2 text-sm font-semibold text-white outline-none transition-colors hover:bg-sidebar-hover focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {title}
        </Link>
      </div>

      <div className="space-y-3 px-3.5 pb-3">
        <label className="flex h-7 items-center gap-2 rounded-md bg-sidebar-input px-2 text-sm text-sidebar-foreground focus-within:ring-2 focus-within:ring-sidebar-ring">
          <Search className="size-4 shrink-0" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchLabel}
            aria-label={searchLabel}
            className="min-w-0 flex-1 bg-transparent text-white outline-none placeholder:text-sidebar-foreground/80"
          />
        </label>

        <div className="flex items-center gap-2.5 px-1">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{profile?.name}</p>
            {domain && <p className="truncate text-xs text-sidebar-muted">{domain}</p>}
          </div>
          <StoreAvatar className="size-8" />
        </div>
      </div>

      <nav className="no-scrollbar flex-1 overflow-y-auto px-3.5 pb-4" aria-label={title}>
        {shown.length === 0 ? (
          <p className="px-2 py-1 text-sm text-sidebar-muted">Nothing matches “{query.trim()}”.</p>
        ) : (
          <ul className="space-y-0.5">
            {shown.map((l) => {
              const Icon = l.icon
              return (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    onClick={onNavigate}
                    aria-current={l.active ? 'page' : undefined}
                    className={cn(
                      'flex h-7 items-center gap-2 rounded-md px-2 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-sidebar-ring',
                      l.nested && !q && 'pl-8',
                      l.active
                        ? 'bg-gradient-to-r from-white/[0.13] to-white/[0.17] text-white [&>svg]:text-white'
                        : 'text-sidebar-foreground hover:bg-sidebar-hover hover:text-white [&>svg]:text-sidebar-muted hover:[&>svg]:text-white'
                    )}
                  >
                    {Icon && <Icon className="size-4 shrink-0" />}
                    <span className="truncate">{l.label}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        )}
      </nav>
    </div>
  )
}
